// Workshop articles — full content from Verna's documents
// Used by the Workshop modal triggered from the "This retreat is for you" section

export const WORKSHOPS = {
  "spiritual-awakening": {
    title: "Spiritual Awakening",
    subtitle: "Discovering Who You Really Are",
    epigraph: "You don't have a soul. YOU ARE A SOUL.",
    epigraphFooter: "Remember, you are God / Source having a human experience. You can be nothing else. You are all that is, and all that will be.",
    sections: [
      {
        heading: "Welcome & Introduction",
        body: "This workshop is an invitation to explore the deepest questions of existence — questions that have driven spiritual seekers throughout history. Who are you, really? What is consciousness? Why are you here? Drawing on the wisdom of Verna Carr's journey — from her first extraordinary encounters with death and spirit in her early twenties, to decades of practice as an energy healing intuitive — this workshop offers both personal story and universal truth.",
      },
      {
        heading: "Workshop Intentions",
        bullets: [
          "Understand what consciousness truly is and how it shapes your reality",
          "Explore the concept of the soul and your Higher Self",
          "Discover what you chose before you were born and why",
          "Begin connecting with your spiritual nature in practical, experiential ways",
          "Open to the understanding that you are far more than your physical body",
        ],
      },
      {
        heading: "Part One — What Is Consciousness?",
        body: "Consciousness is simply energy — energy that is aware of its own existence. You are Divine Consciousness, or Source, having a human experience. In a timeless state, there is no change, there is no growth and no discovery. So God / Consciousness created infinite mirrors reflecting back to itself, experiencing itself from infinite perspectives. When you came through the veil and were born into a human body, you chose to forget that you were God having a human experience. You imposed on yourself a kind of amnesia, so you could genuinely have a truly human experience — with real discovery, real growth, real feeling.",
      },
      {
        heading: "The Template of Your Life",
        body: "Before you were born into this physical body, you were a soul. You wanted a physical human experience, so you created the template for your life. You chose everything:",
        bullets: [
          "Your parents and family dynamics",
          "Your gender, culture, and country of birth",
          "Your birth date, aligned with astrological configurations",
          "Your life circumstances, obstacles, lessons, and joys",
          "The other souls who would co-create this life with you",
          "Even your exit points — when and how you would die",
        ],
        bodyAfter: "You also packed into your life template unresolved issues and traumas that have impacted previous versions of you — which is why some people have irrational fears or phobias, strong unexplained beliefs, or feel inexplicably drawn to certain places or periods in history.",
      },
      {
        heading: "You Are 70% Non-Physical",
        body: "Most of your consciousness exists in different realms and dimensions. You are 70% non-physical and only 30% physical, connected through silver cords. Your Higher Self — the non-physical part of who you are — has the bigger picture and is always looking after you. When you are aligned with your Higher Self, life seems to flow, and you will be in the right place, meeting the right people in perfect synchronicity.",
      },
      {
        heading: "Lemon Tree Analogy",
        body: "One way of understanding body and soul is through the analogy of the lemon tree — the only tree to have blossoms, green fruit, and ripe fruit all at the same time. Imagine you are one lemon on a great tree, having your own experience, separate and sovereign, yet completely connected.",
        bullets: [
          "Each lemon = one human life or incarnation",
          "Each incarnation has its own Higher Self and life plan",
          "The lemon tree (aware of all its fruit) = the soul",
          "The root system connecting trees = the quantum fields of existence",
        ],
        bodyAfter: "Outside of three-dimensional reality, time does not exist. All your incarnations are happening all at once. This is quantum understanding — stepping beyond the linear into a far greater truth.",
      },
      {
        heading: "Part Two — High & Low Vibration",
        body: "Everything is energy, and energy vibrates at different frequencies. Your thoughts, feelings, and experiences all have a vibrational frequency that expands or contracts your life force.",
      },
      {
        heading: "High Vibrational Energy",
        body: "High vibrational energy expands the flow of your life force, consciousness, and connection to Source. It creates:",
        bullets: [
          "Health, well-being, clarity, and direction",
          "Harmony, love, support, and balance",
          "Growth, expansion, and a feeling of ease and grace",
          "Synchronicity — being in the right place at the right time",
          "Joy, peace, fun, gratitude, and excitement",
        ],
      },
      {
        heading: "Low Vibrational Energy",
        body: "Low vibrational energy contracts the flow of your life force and creates self-perpetuating loops of limiting beliefs that keep you stuck. These include:",
        bullets: [
          "Stress, fear, anger, shame, and rejection",
          "Depression, abuse, and feelings of isolation",
          "Physical illness and disease in the body",
        ],
        bodyAfter: "When energy doesn't move it creates self-perpetuating loops of limiting beliefs that keep you stuck — eventually leading to pain, illness and disease.",
      },
      {
        heading: "Part Three — Verna's Story",
        body: "Verna's journey into spiritualism and death happened in her early twenties. In a matter of months, three people close to her died. Each time, she experienced something that defied ordinary explanation — feeling a presence, knowing the exact moment of passing, receiving messages through an inner knowing that couldn't be dismissed. These experiences led her to spiritual churches, to mediums, to hundreds of books, and ultimately to a life dedicated to exploring what lies beyond the three-dimensional world. Over 35 years of practice as an energy healing intuitive, she has developed the ability to see, feel, and work with consciousness in ways most people have never imagined.",
      },
      {
        heading: "Reflection & Journalling Prompts",
        bullets: [
          "What questions about life, death, and consciousness have you carried for years? Write them down without judgment.",
          "When in your life have you felt deeply connected to something greater than yourself? Describe that experience.",
          "What gifts, talents, or abilities did you arrive in this world with? How might they relate to your soul's purpose?",
          "What irrational fears or strong unexplained feelings do you carry? Could these be carried in from other lifetimes?",
          "If you genuinely chose your life before you were born, what do you think your soul most wanted to experience or learn?",
        ],
      },
      {
        heading: "Closing Affirmation",
        body: "Stand still, and take a breath. Remember you are God in a body. What do you want to do with this? Every day you have to think to yourself: what are you going to do today? What are you going to think today? How much of yourself are you willing to express in your life? You are a magnificent, divine being on a journey of self-discovery. This is only the beginning.",
      },
    ],
  },

  "energy-healing": {
    title: "Energy Healing & Expansion",
    subtitle: "Working With Your Body, Energy, and the Quantum Field",
    epigraph: "A healer is not someone you go to for healing.",
    epigraphFooter: "A healer is someone that triggers within you your own ability to heal yourself.",
    sections: [
      {
        heading: "Welcome & Overview",
        body: "We are far more than our physical bodies. We are energy beings, operating in a quantum field of infinite possibility. This workshop explores the science and practice of conscious energy healing — what it is, how it works, and how you can begin to work with it in your own life. You are the ultimate healer. Everything we explore together in this workshop points back to that fundamental truth.",
      },
      {
        heading: "Workshop Intentions",
        bullets: [
          "Understand what intuitive energy healing is and how it works",
          "Learn the connection between thoughts, emotions, and physical health",
          "Explore the body's energy centres (chakras) and what they represent",
          "Experience practical energy healing exercises and techniques",
          "Understand why people sometimes resist healing — and how to move through that",
          "Receive and practise basic energy healing facilitation",
        ],
      },
      {
        heading: "Part One — What Is Intuitive Energy Healing?",
        body: "Humans are not a collection of body parts — we are whole beings with both physical and non-physical aspects. Conscious energy healing works on the premise that we are soul, mind, and body working together in unison. When these are in balance and harmony, the physical body is healthy, vibrant, and rarely gets sick.",
      },
      {
        heading: "The Energy Model of Health",
        body: "Think of energy like water:",
        bullets: [
          "Steam or mist = Spirit state of consciousness — molecules moving fast, free-flowing",
          "Liquid water = Mind state of consciousness — still flowing, but slower and denser",
          "Ice = Physical state of consciousness — solidified, not moving",
        ],
        bodyAfter: "High vibrational thoughts and emotions like love, appreciation, and gratitude create free-flowing energy — healthy minds and healthy bodies. The more negative the thoughts and feelings, the slower the energy moves, until it becomes sluggish or solidified within the body, creating pain, illness, or disease. All pain is resistance to your natural self. The body is not designed to be a storage unit.",
      },
      {
        heading: "The Body's Conscious Cells",
        body: "The cells in your body are also conscious and are always listening. They will behave according to the instructions they are given. What are you telling your cells right now?",
        bullets: [
          "\"He's a pain in the neck\" — where does that show up?",
          "\"My heart's not in it\" — what does that do to your heart chakra?",
          "\"I'm sick and tired\" — what message does the body receive?",
        ],
        bodyAfter: "By talking with your cells consciously, you can create health and well-being where there is disease. You can ask your cells to release old baggage. This is the foundation of conscious healing.",
      },
      {
        heading: "Part Two — Mind, Body & Soul as a Trilogy",
        body: "When mind, body, and soul work together as a trilogy, they will always take you to where you want to go, creating and manifesting any experience you want to have.",
        bullets: [
          "Mind — Third Eye / Brain · knowledge, wisdom, reasoning, analysis",
          "Body — Heart Chakra · passion, emotion, love, self-worth, guidance",
          "Soul — Solar Plexus · intuition, multi-dimensional consciousness, life force, creativity",
        ],
        bodyAfter: "When all three centres work together in balance, so-called miracles happen. You live your life shining your light bright, filled with peace and joy, free from pain and illness.",
      },
      {
        heading: "Body Testing — Learning to Listen",
        body: "Your body will never lie to you — it is the ultimate lie detector. Three simple ways to test what resonates:",
        bullets: [
          "Finger strength test: form an 'O' with thumb and index finger on each hand, interlock them, and ask a yes/no question. Strong = yes. Weak = no.",
          "Body pendulum: stand facing a wall, balance your energy, cross arms on chest, ask a question. Forward = yes. Backward = no.",
          "Coin toss: toss a coin and notice which side you hope it lands on. That is your answer.",
        ],
      },
      {
        heading: "Part Three — Practical Energy Healing Tools",
        bullets: [
          "Stay in the now moment",
          "No judgment — only discernment. Ask: 'Does this resonate with me?'",
          "Be mindful of what you are choosing and creating through thoughts, words, and actions",
          "Follow your excitement — this is your soul guiding you",
          "Commit to a daily practice that nourishes you",
          "Find ways to be thankful and appreciative for the life you have",
          "When something triggers you, say: 'Thank you. I am aware of that. I am bringing myself back to peace.'",
          "Believe in yourself. You are a Divine, Magnificent Being",
        ],
      },
      {
        heading: "Part Four — The Gratitude Chakra Meditation",
        body: "This beautiful guided meditation works through each of the seven energy centres, filling each with light, gratitude, and appreciation. It can be used as a tune-up in five minutes or explored deeply for as long as you wish.",
        bullets: [
          "Root Chakra — Red — Safety, comfort, security",
          "Sacral Chakra — Orange — Pleasure, joy, excitement",
          "Solar Plexus — Yellow — Decision-making, creativity, wisdom",
          "Heart Chakra — Green — Love, balance, peace",
          "Throat Chakra — Blue — Expression, speaking your truth",
          "Third Eye — Indigo — Intuition, imagination, clarity",
          "Crown Chakra — Violet — Divine connection, Higher Self",
        ],
      },
      {
        heading: "Closing Affirmation",
        body: "When you let go, peace happens. Align yourself to this, for it is your natural state of being. You are communicating with your body all the time. Be mindful and conscious of what messages you are sending to yourself. Your cells are listening. You are the healer you have been waiting for.",
      },
    ],
  },

  "awakening-consciousness": {
    title: "Awakening to Consciousness",
    subtitle: "Navigating the Dark Night, Death, Forgiveness & Manifestation",
    epigraph: "It doesn't matter how your life has led you to this point.",
    epigraphFooter: "What matters is the choices you now make. Are you a victim? Or are you the creator of your life?",
    sections: [
      {
        heading: "Welcome & Overview",
        body: "This workshop is about the deeper layers of awakening — the places we don't often talk about in polite company. The Dark Night of the Soul. Death and what lies beyond. The radical power of forgiveness. And the quantum science of consciously creating the life you want. Awakening is not always comfortable. Sometimes it looks like falling apart. But within every crisis is an invitation to wake up to a greater truth about who you are.",
      },
      {
        heading: "Workshop Intentions",
        bullets: [
          "Understand the Dark Night of the Soul as a gateway, not a dead end",
          "Explore the truth about death and what consciousness experiences beyond the body",
          "Discover the healing power of forgiveness — for others and for yourself",
          "Learn the seven steps to consciously manifesting your desired reality",
          "Step into the role of creator rather than victim in your own life",
        ],
      },
      {
        heading: "Part One — The Dark Night of the Soul",
        body: "Most people have experienced some form of suffering — loss, feeling unloved, relationship failure, rejection, abuse. These experiences create what Verna calls the 'Hungry Ghosts' — a feeling of emptiness that wants to be filled. The Dark Night of the Soul is when these suppressed experiences rise to the surface. It can bring us to our knees. But here is the truth: you purposefully chose these experiences as part of your soul's growth. Your lessons for growth are self-selected.",
      },
      {
        heading: "The Way Through",
        body: "There is only one way out of the depression of the dark night of the soul — and it is inward:",
        bullets: [
          "Love yourself",
          "Forgive all those who have hurt you",
          "Forgive yourself",
          "Triple A: Acceptance, Acknowledgement, Allow — accept and acknowledge what the experience has taught you, and allow yourself to move on",
          "Know that you have a choice of how you want to be and act NOW",
        ],
        bodyAfter: "The tools of empowerment are gratitude, laughter, and changing whatever is not working for you. Visualise the version of you who has moved through this. Imagine yourself doing this — and then act as that person would act.",
      },
      {
        heading: "Part Two — Death & What Lies Beyond",
        body: "Verna's extraordinary gift is her ability to work at the intersection of life and death. When you die: you will float above your physical body. You will travel down a tunnel, see a bright light, or cross a bridge between realities. You will be met by all those you have loved, including your pets. There is no fear, no judgment, no anger, no guilt — only unconditional love. You then sit with trusted advisors and have a life review. Every thought, every emotion, every action, every consequence is experienced from a place of neutrality. There is no punishment. Only loving understanding.",
      },
      {
        heading: "Three Common Messages from Loved Ones Who Have Passed",
        bullets: [
          "I am ok — well and thriving, still around you, still connected",
          "Death is nothing to be afraid of — simply a transition between realities",
          "There are no rules around grieving — but please do not let my death steal your life",
        ],
      },
      {
        heading: "Practical Ways to Navigate Grief",
        bullets: [
          "Allow yourself to cry freely — tears are not weakness",
          "Share memories with others who loved them",
          "Find support — grief can be very isolating",
          "Be gentle and patient with yourself",
          "Journal: write letters to the person who has passed",
          "Take care of your body — rest, eat well, gentle exercise",
        ],
      },
      {
        heading: "Part Three — The Power of Forgiveness",
        body: "Unforgiveness is like taking poison. Not only does it energetically affect the one you are angry towards — it equally impacts you as the one thinking those thoughts. Getting on with your life in the happiest, most joyful way possible doesn't mean that you condone what was done to you — it means that you are strong and empowered.",
      },
      {
        heading: "Forgiveness Does Not Mean…",
        bullets: [
          "...condoning the actions of another",
          "...saying what happened was okay",
          "...allowing the person back into your life",
          "...pretending the pain did not happen",
        ],
      },
      {
        heading: "Forgiveness Does Mean…",
        bullets: [
          "...finding your peace with what has happened",
          "...freeing yourself from being defined by the past",
          "...allowing energy to be released from your body",
          "...forgiving yourself for the hurt you have caused — releasing guilt and shame",
        ],
      },
      {
        heading: "The Hawaiian Ho'oponopono Healing Prayer",
        body: "A simple, powerful practice. Repeat for anyone you need to release — including yourself:",
        bullets: [
          "I'm sorry",
          "Please forgive me",
          "Thank you",
          "I love you",
        ],
      },
      {
        heading: "Part Four — The Seven Steps to Manifestation",
        bullets: [
          "Vision — have a clear idea, dream, or picture of what you want. Ask yourself WHY you want it.",
          "Desire — are you excited? Do you have strong positive emotions that this is possible?",
          "Belief — do you believe this is possible? Do you believe you deserve it?",
          "Acceptance — totally accept yourself and the new belief to be true, without doubt.",
          "Intention — make a conscious choice. Ensure your thoughts and feelings are aligned with what you are asking for.",
          "Action — act as if it has already happened. The universe doesn't understand the future tense.",
          "Allowance — allow the flow without expecting it to happen in a certain way.",
        ],
      },
      {
        heading: "Closing Affirmation — from Tabaash",
        body: "From the Lord God of my Being, I now choose to be totally liberated, mind, body and spirit: of all that limits me, of all that I am attached to that no longer serves me. I ask forgiveness of all the people that I have hurt. I forgive all the people who have hurt me. I forgive myself for all the ways that I have hurt myself. Now I choose to be at peace. Always. So be it. You are the creator of your life. Choose wisely. Choose boldly. Choose love.",
      },
    ],
  },

  "opening-heart": {
    title: "Opening the Heart",
    subtitle: "Love, Gratitude, Spirit Guides & Your Journey of Self-Discovery",
    epigraph: "The longest and hardest journey you will ever make is to go from your mind into your heart centre.",
    epigraphFooter: "Creative Source is love and created you from love. If you exist, you are loved unconditionally.",
    sections: [
      {
        heading: "Welcome & Overview",
        body: "This final workshop is about coming home — to your heart. Everything we have explored across these workshops has pointed in this direction. Consciousness, healing, awakening — all roads lead to love. Not romantic love, but the unconditional love that is the very substance of who you are. Today we open fully to unconditional love, connect with our spirit guides and Higher Self, explore the transformative power of gratitude, and celebrate our journey of self-discovery.",
      },
      {
        heading: "Workshop Intentions",
        bullets: [
          "Experience unconditional love as your natural state of being",
          "Connect with spirit guides and Higher Self through meditation",
          "Understand the transformative power of gratitude as a vibrational practice",
          "Complete a self-evaluation to integrate all workshop learnings",
          "Leave with a personalised plan for your ongoing spiritual practice",
        ],
      },
      {
        heading: "Part One — Unconditional Love",
        body: "Creative Source / God is Love. We are expressions of, and created from, that love. We are love. Love is consciousness. Without love, we become disconnected from who we truly are. Dr Masaru Emoto's experiments showed that human words, thoughts, sounds, and intentions literally change the molecular structure of water — and we are approximately 80% water. We are expressions of Unconditional Love. Why then do we hold it back? It is our true nature to allow love to flow freely through us. When we do this, we maintain a deep connection with our Higher Self.",
      },
      {
        heading: "Unconditional Love Affirmations",
        bullets: [
          "I AM love",
          "I AM self-love",
          "I AM Divine love",
          "I AM the infinite love of all that is and all that will be",
        ],
      },
      {
        heading: "Part Two — Connecting With Spirit Guides & Higher Self",
        body: "Spirit guides are high vibrational non-physical energy. Before you were born, guides were assigned to you who would help show and direct you for your highest good and greatest growth. You may feel you are imagining your guides at first — the imagination is exactly where the magic happens, and exactly how they communicate.",
      },
      {
        heading: "How to Know Your Guide is Genuine",
        bullets: [
          "They come with the highest vibration of unconditional love, kindness, and compassion",
          "You will feel your heart chakra expand",
          "Nothing they do will hurt you or harm you",
          "They only act in the highest interest of all concerned",
          "They will never tell you what to do — they encourage you to find your own answers",
          "They don't come from ego",
        ],
      },
      {
        heading: "Part Three — The Power of Gratitude",
        body: "The universe works by vibration. What you put out gets mirrored back to you. Gratitude is high vibration because it allows you to appreciate what is already in your life — and so you create more of the same. The tide lifts all boats. Gratitude is an investment in your own experience, your health and well-being, and your life flowing easier.",
      },
      {
        heading: "Daily Gratitude Practice",
        body: "Every morning, every evening — as often as possible — take a moment, take a deep breath, and ask yourself: What are the three things I am most grateful for RIGHT NOW?",
        bullets: [
          "Recognise what is already in your life that you appreciate",
          "Appreciate the loved ones you have and the things they do for you",
          "Acknowledge the positive things that happen in your life",
          "Enjoy life's small treasures — feel abundant",
          "Be happy now — you don't have to chase the next thing to be happy",
        ],
      },
      {
        heading: "Part Four — Self-Evaluation: Learning to Value Yourself",
        body: "Take your time with these questions. The more honest you are with yourself, the more empowered you become to make different choices.",
        bullets: [
          "If you loved yourself unconditionally — what would that look like?",
          "Whom would you allow into your life?",
          "What would you say to yourself?",
          "What would be acceptable and unacceptable?",
          "What have you learned about your own consciousness?",
          "How do you align your mind, body, and soul to work together?",
          "What is your Higher Self saying to you right now?",
          "Do you believe you can heal yourself?",
        ],
      },
      {
        heading: "Moving Forward",
        bullets: [
          "Who do you need to forgive?",
          "Are you a victim or a creator?",
          "What are you grateful for right now?",
          "What action steps are you taking to create the life you want?",
        ],
      },
      {
        heading: "Closing: The Great Awakening",
        body: "We are experiencing huge cosmic energetic shifts as more light enters this planet. Never before have there been so many awake and aware people. Through your own healing, your own awakening, your own opening of the heart — you are changing not only your own consciousness but also the consciousness of this planet. Blessed old soul, blessed one that has been here so many times before. You still do not recognise the magnificence of who you are. You can rip away the perimeters, the boundaries, the limitations. You are far greater than even you think is possible. — Verna Carr",
      },
      {
        heading: "Final Affirmation — from Tabaash",
        body: "From the Lord God of my Being, I AM love, light and peace. I give the Source inside of me permission to express the highest conditions: to love me, to guide me, to nurture me, to whisper in my ear intuitively, to connect me to my own knowledge and wisdom, and to align my thoughts and feelings with the abundance I want and deserve. When I speak this and be this, I create as the EMPOWERED being that I AM.",
      },
    ],
  },
};
